import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import TargetPushPanel from './rescuePush/TargetPushPanel';
import useRescuePushTarget from '../hooks/useRescuePushTarget';
import useRescuePushRecipients from '../hooks/useRescuePushRecipients';
import { DEFAULT_TRACK_FORM, defaultExpiresAtLocalInput } from '../utils/rescuePush';

/**
 * 指挥页搜索坐标「设为目标」后：复用救援推送「目标推送」面板（组织/小组/任务受众 + POST /api/rescue/target）
 */
export default function DashboardRescueSearchTargetPushModal({
  open,
  lat,
  lng,
  getMap,
  onClose,
  onPushed,
  onClearDraftTargets
}) {
  const mapRef = useRef(null);
  const displayTargetOnMapRef = useRef(() => {});
  const markTargetOnMapRef = useRef(() => {});
  const [, setTrackForm] = useState({ ...DEFAULT_TRACK_FORM });

  displayTargetOnMapRef.current = (target) => {
    onPushed?.(target);
  };
  markTargetOnMapRef.current = () => {
    /* 指挥页已有待推送水滴，表单内改坐标时不再另画圆点图层 */
  };

  useEffect(() => {
    mapRef.current = typeof getMap === 'function' ? getMap() : null;
  }, [getMap, open]);

  const target = useRescuePushTarget({
    mapRef,
    markTargetOnMapRef,
    displayTargetOnMapRef
  });
  const recipients = useRescuePushRecipients({
    setTargetForm: target.setTargetForm,
    setTrackForm
  });

  useEffect(() => {
    if (!open) return;
    const la = Number(lat);
    const ln = Number(lng);
    if (!Number.isFinite(la) || !Number.isFinite(ln)) return;
    target.setTargetForm((form) => ({
      ...form,
      lat: String(la),
      lng: String(ln),
      expires_at: form.expires_at || defaultExpiresAtLocalInput(),
      map_link: ''
    }));
    target.setTargetResult(null);
    // 仅在打开/坐标变化时预填
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lat, lng]);

  useEffect(() => {
    if (open && target.targetResult?.success) {
      // 成功后短延迟关层，方便看到「推送成功」
      const t = setTimeout(() => {
        onClose?.();
      }, 600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, target.targetResult, onClose]);

  return (
    <Modal
      title="目标推送"
      open={!!open}
      onCancel={onClose}
      footer={null}
      width={560}
      destroyOnClose={false}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto', paddingTop: 12 } }}
      zIndex={1200}
    >
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
        与「救援推送 → 目标推送」相同：可选组织/小组或任务/小组，提交走同一接口。
      </div>
      <TargetPushPanel
        organizations={recipients.organizations}
        groups={recipients.groups}
        users={recipients.users}
        missions={recipients.missions || []}
        targetForm={target.targetForm}
        setTargetForm={target.setTargetForm}
        handleMultiSelect={recipients.handleMultiSelect}
        selectAll={recipients.selectAll}
        clearSelection={recipients.clearSelection}
        filterGroupsByOrg={recipients.filterGroupsByOrg}
        filterUsersByGroup={recipients.filterUsersByGroup}
        handleFormChange={target.handleFormChange}
        handleCoordinatePaste={target.handleCoordinatePaste}
        handleCoordinateParse={target.handleCoordinateParse}
        handleMapLinkChange={target.handleMapLinkChange}
        handleMapLinkParse={target.handleMapLinkParse}
        mapLinkParsing={target.mapLinkParsing}
        handleTargetPush={target.handleTargetPush}
        targetLoading={target.targetLoading}
        clearAllTargets={() => {
          onClearDraftTargets?.();
          onClose?.();
        }}
        targetResult={target.targetResult}
        embedded
      />
    </Modal>
  );
}
